import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Inbox, MessageSquare, Send, AlertTriangle, CheckCircle2,
  ChevronRight, X, ThumbsUp, ThumbsDown, Minus, ArrowUpRight,
  BarChart3, Filter,
} from 'lucide-react';
import {
  assistantApi,
  type Conversation,
  type ConversationStatus,
  type Message,
} from '../../../api/assistant';
import { isFeatureDisabledError } from '../../../api/_http';

// ─── Status helpers ─────────────────────────────────────────────────────────

const STATUS_COLORS: Record<ConversationStatus, string> = {
  active: 'bg-blue-100 text-blue-700',
  escalated: 'bg-red-100 text-red-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-neutral-100 text-neutral-500',
};

const STATUS_ICONS: Record<ConversationStatus, React.ReactNode> = {
  active: <MessageSquare size={12} />,
  escalated: <AlertTriangle size={12} />,
  resolved: <CheckCircle2 size={12} />,
  closed: <Minus size={12} />,
};

// ─── Hooks ──────────────────────────────────────────────────────────────────

// Don't retry when the assistant service is intentionally disabled in nginx —
// retrying would just spam the network tab with identical 503s.
const skipRetryIfDisabled = (failureCount: number, error: unknown) =>
  !isFeatureDisabledError(error) && failureCount < 2;

function useConversations(status?: ConversationStatus) {
  return useQuery({
    queryKey: ['assistant-conversations', status],
    queryFn: () => assistantApi.listConversations({ status }),
    staleTime: 15_000,
    retry: skipRetryIfDisabled,
  });
}

function useMessages(conversationId: string | null) {
  return useQuery({
    queryKey: ['assistant-messages', conversationId],
    queryFn: () => assistantApi.getMessages(conversationId!),
    enabled: !!conversationId,
    refetchInterval: (query) =>
      isFeatureDisabledError(query.state.error) ? false : 5_000,
    retry: skipRetryIfDisabled,
  });
}

function useAdminMetrics() {
  return useQuery({
    queryKey: ['assistant-admin-metrics'],
    queryFn: () => assistantApi.getAdminMetrics(),
    staleTime: 30_000,
    retry: skipRetryIfDisabled,
  });
}

// ─── Message Bubble ─────────────────────────────────────────────────────────

function MessageBubble({ message, onRate }: { message: Message; onRate: (rating: 'helpful' | 'unhelpful') => void }) {
  const isUser = message.role === 'user';
  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[70%] rounded-xl px-4 py-2.5 text-sm ${
        isUser
          ? 'bg-indigo-600 text-white rounded-br-sm'
          : 'bg-neutral-100 text-neutral-800 rounded-bl-sm'
      }`}>
        <p className="whitespace-pre-wrap">{message.content}</p>
        <div className="flex items-center justify-between mt-1.5">
          <span className={`text-[10px] ${isUser ? 'text-indigo-200' : 'text-neutral-400'}`}>
            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
          {!isUser && message.role === 'assistant' && (
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={() => onRate('helpful')}
                className={`p-0.5 rounded ${message.rating === 'helpful' ? 'text-green-600' : 'text-neutral-400 hover:text-green-600'}`}
              >
                <ThumbsUp size={11} />
              </button>
              <button
                onClick={() => onRate('unhelpful')}
                className={`p-0.5 rounded ${message.rating === 'unhelpful' ? 'text-red-500' : 'text-neutral-400 hover:text-red-500'}`}
              >
                <ThumbsDown size={11} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Conversation Thread Panel ──────────────────────────────────────────────

function ConversationThread({ conversation, onClose }: { conversation: Conversation; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data: messagesData, isLoading } = useMessages(conversation.id);
  const [input, setInput] = useState('');

  const sendMut = useMutation({
    mutationFn: (content: string) => assistantApi.sendMessage(conversation.id, content),
    onSuccess: () => {
      setInput('');
      queryClient.invalidateQueries({ queryKey: ['assistant-messages', conversation.id] });
    },
  });

  const escalateMut = useMutation({
    mutationFn: () => assistantApi.escalate(conversation.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistant-conversations'] });
    },
  });

  const rateMut = useMutation({
    mutationFn: ({ messageId, rating }: { messageId: string; rating: 'helpful' | 'unhelpful' }) =>
      assistantApi.rateMessage(conversation.id, messageId, rating),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assistant-messages', conversation.id] });
    },
  });

  const messages = messagesData?.messages ?? [];

  function handleSend() {
    if (!input.trim()) return;
    sendMut.mutate(input.trim());
  }

  return (
    <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-xl z-40 border-l border-neutral-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-100 shrink-0">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[conversation.status]}`}>
            {STATUS_ICONS[conversation.status]} {conversation.status}
          </span>
          {conversation.intent && (
            <span className="text-xs text-neutral-500">Intent: {conversation.intent}</span>
          )}
          {conversation.confidence !== null && (
            <span className="text-xs text-neutral-400">({Math.round(conversation.confidence * 100)}%)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {conversation.status === 'active' && (
            <button
              onClick={() => escalateMut.mutate()}
              disabled={escalateMut.isPending}
              className="flex items-center gap-1 px-2.5 py-1 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
            >
              <ArrowUpRight size={12} /> Escalate
            </button>
          )}
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X size={18} /></button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-5 space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div className="h-10 w-48 bg-neutral-100 rounded-xl animate-pulse" />
              </div>
            ))}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-neutral-400 py-8">No messages yet</div>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              onRate={(rating) => rateMut.mutate({ messageId: m.id, rating })}
            />
          ))
        )}
      </div>

      {/* KB Citations */}
      {conversation.kb_citations.length > 0 && (
        <div className="px-5 py-2 border-t border-neutral-50 bg-neutral-50/50">
          <p className="text-[10px] text-neutral-400 mb-1">KB Articles cited:</p>
          <div className="flex flex-wrap gap-1">
            {conversation.kb_citations.map((c, i) => (
              <span key={i} className="text-[10px] px-1.5 py-0.5 bg-white border border-neutral-200 rounded text-neutral-500">
                {c}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-neutral-100 p-4 shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            placeholder="Type a message..."
            className="flex-1 px-3 py-2 border border-neutral-200 rounded-lg text-sm resize-none h-16 focus:ring-1 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || sendMut.isPending}
            className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function InboxPage() {
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | ''>('');
  const [selected, setSelected] = useState<Conversation | null>(null);

  const { data: convsData, isLoading, isError, error } = useConversations(statusFilter || undefined);
  const { data: metrics } = useAdminMetrics();

  const conversations = convsData?.conversations ?? [];
  const assistantDisabled = isFeatureDisabledError(error);

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Inbox size={20} className="text-indigo-600" />
              <h1 className="text-xl font-semibold text-neutral-900">Inbox</h1>
            </div>
            <p className="text-sm text-neutral-500">AI assistant conversations and escalations</p>
          </div>
        </div>

        {/* Metrics bar — hide when feature is disabled */}
        {metrics && !assistantDisabled && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: 'Active', value: metrics.active_conversations, color: 'text-blue-600', bg: 'bg-blue-50' },
              { label: 'Escalated', value: metrics.escalated_conversations, color: 'text-red-600', bg: 'bg-red-50' },
              { label: 'Resolution Rate', value: `${Math.round(metrics.resolution_rate * 100)}%`, color: 'text-green-600', bg: 'bg-green-50' },
              { label: 'Avg Confidence', value: `${Math.round(metrics.avg_confidence * 100)}%`, color: 'text-indigo-600', bg: 'bg-indigo-50' },
            ].map((s) => (
              <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
                <p className="text-2xl font-semibold mb-0.5"><span className={s.color}>{s.value}</span></p>
                <p className="text-xs text-neutral-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4">
          <Filter size={14} className="text-neutral-400" />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as ConversationStatus | '')}
            className="text-xs border border-neutral-200 rounded-lg px-2 py-1.5 bg-white text-neutral-700">
            <option value="">All conversations</option>
            <option value="active">Active</option>
            <option value="escalated">Escalated</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* Conversation list */}
        <div className="bg-white border border-neutral-100 rounded-xl shadow-sm">
          {isLoading ? (
            <div className="p-6 space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <div className="h-4 w-16 bg-neutral-100 rounded animate-pulse" />
                  <div className="h-4 flex-1 bg-neutral-100 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-neutral-100 rounded animate-pulse" />
                </div>
              ))}
            </div>
          ) : assistantDisabled ? (
            <div className="p-10 text-center">
              <Inbox size={32} className="mx-auto text-neutral-300 mb-2" />
              <p className="text-sm font-medium text-neutral-600">Inbox not available</p>
              <p className="text-xs text-neutral-400 mt-1">
                The AI assistant service is not enabled in this environment.
              </p>
            </div>
          ) : isError ? (
            <div className="p-10 text-center text-sm text-red-600">
              Failed to load conversations. Check that the assistant service is running.
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-10 text-center">
              <MessageSquare size={32} className="mx-auto text-neutral-300 mb-2" />
              <p className="text-sm text-neutral-400">No conversations found</p>
            </div>
          ) : (
            <div className="divide-y divide-neutral-50">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => setSelected(conv)}
                  className="w-full flex items-center gap-4 px-5 py-3 hover:bg-neutral-50 text-left transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[conv.status]}`}>
                        {STATUS_ICONS[conv.status]} {conv.status}
                      </span>
                      {conv.intent && (
                        <span className="text-xs text-neutral-500 truncate">{conv.intent}</span>
                      )}
                    </div>
                    <p className="text-xs text-neutral-400">
                      {conv.message_count ?? 0} messages · {conv.last_message_at ? new Date(conv.last_message_at).toLocaleString() : 'No messages'}
                    </p>
                  </div>
                  {conv.escalation_level > 0 && (
                    <span className="text-xs text-red-500 font-medium">L{conv.escalation_level}</span>
                  )}
                  <ChevronRight size={16} className="text-neutral-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Thread panel */}
      {selected && <ConversationThread conversation={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
